import { Injectable } from '@angular/core';
import { InternalDatabaseService } from '../internal/internal-database.service';
import { Observable, combineLatest } from 'rxjs';
import { TheatrePatientMaterials } from './theatre-patient-materials';
import { Stock } from '@stockcare-storage/stock/stock';
import { IMaterial } from '@stockcare-models/IMaterial';
import { ITheatrePatientVisit } from '@stockcare-models/ITheatrePatientVisit';
import { take } from 'rxjs/operators';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { AuthenticationService } from '@stockcare-services/index';

@Injectable({
  providedIn: 'root',
})
export class TheatrePatientMaterialsStorageService {
  readonly table: string;
  readonly index: string;
  readonly packIdIndex: string;
  readonly packBasketIdIndex: string;
  readonly theatrePatientIdIndex: string;
  enableLocaldb: boolean;
  patientMaterials: TheatrePatientMaterials[] = [];

  constructor(private internalDatabaseService: InternalDatabaseService,
    private readonly settingsService: SettingsService, private authenticationService: AuthenticationService,) {
    const table = TheatrePatientMaterials.getTableDefinition();
    this.table = table.TableName;
    this.index = table.Indexes[0].Name;
    this.packIdIndex = table.Indexes[1].Name;
    this.theatrePatientIdIndex = table.Indexes[2].Name;
    this.packBasketIdIndex = table.Indexes[3].Name;
  }

  add(theatrePatientId: number, material: TheatrePatientMaterials): Observable<boolean> {
    return new Observable((observer) => {
      const theatrePatientService = new TheatrePatientMaterials();
      theatrePatientService.PackId = material.PackId;
      theatrePatientService.TheatrePatientId = theatrePatientId;
      theatrePatientService.MaterialId = material.Id;
      theatrePatientService.Quantity = material.Quantity;
      theatrePatientService.OriginalQuantity = material.OriginalQuantity;
      theatrePatientService.Barcode = material.Barcode;
      theatrePatientService.UnitOfMeasure = material.UnitOfMeasure;
      theatrePatientService.Name = material.Name;
      theatrePatientService.StockLocationId = material.StockLocationId;
      theatrePatientService.PackBasketId = material.PackBasketId;
      theatrePatientService.Username = material.Username;
      if(this.enableLocaldb){
          this.internalDatabaseService
          .add(this.table, theatrePatientService)
          .then(() => {
              observer.next(true);
          })
          .catch((error) => {
            console.error(error);
            observer.error(false);
          });
      }
      else{
        theatrePatientService.Id = this.patientMaterials.length + 1;
        this.patientMaterials.push(theatrePatientService);
        observer.next(true);
      }
    });
  }

  addToBasket(theatrePatientId: number, material: TheatrePatientMaterials, quantity: number): Observable<boolean> {
    return new Observable((observer) => {
      material.Quantity = quantity;
      this.add(theatrePatientId, material)
        .subscribe(
          () => {
            observer.next(true);
          },
          (error) => observer.error(false)
        );
    });
  }

  updateBasket(theatrePatientId: number, material: TheatrePatientMaterials, quantity: number): Observable<boolean> {
    return new Observable((observer) => {
      if(this.enableLocaldb){
        this.internalDatabaseService
          .getAllByIndex<TheatrePatientMaterials>(this.table, this.theatrePatientIdIndex, theatrePatientId)
          .then((result) => {
            const materialToUpdate = result.find((x) => x.MaterialId === material.Id);
            materialToUpdate.Quantity = quantity;
            this.internalDatabaseService
              .update(this.table, materialToUpdate)
              .then(() => {
                observer.next(true);
              })
              .catch((error) => {
                observer.error(false);
              });
          });
        }
        else{
          let existingMaterial = this.patientMaterials.find((x)=> x.TheatrePatientId === theatrePatientId && x.MaterialId === material.MaterialId);
          if(existingMaterial){
            existingMaterial.Quantity = quantity;
            observer.next(true);
          }
          else{
            observer.error(false);
          }
        }
    });
  }

  addUpdateMaterials(theatrePatient: ITheatrePatientVisit): Observable<boolean> {
    this.getSettings();
    return new Observable((observer) => {
      const subscriptions: Array<Observable<boolean>> = [];

      if (theatrePatient.materials && theatrePatient.materials.length > 0) {
        theatrePatient.materials.forEach((x) => {
          subscriptions.push(this.add(theatrePatient.id, this.map(theatrePatient.id, x)));
        });
      } else {
        observer.next();
      }

      combineLatest(subscriptions).subscribe(() => {
        observer.next();
      });
    });
  }

  update(theatrePatientId: number, barcode: string, quantity: number) {
    return new Observable((observer) => {
      if(this.enableLocaldb){
      this.internalDatabaseService
        .getAllByIndex<TheatrePatientMaterials>(this.table, this.theatrePatientIdIndex, theatrePatientId)
        .then((existingMaterials) => {
          const existingMaterial = existingMaterials.find(
            (x) => x.Barcode === barcode && (x.PackId === undefined || x.PackId === null || x.PackId === 0)
          );
          existingMaterial.Quantity = quantity;
          existingMaterial.Username = this.authenticationService.getUsername();

          this.internalDatabaseService
            .update(this.table, existingMaterial)
            .then(() => {
              observer.next(true);
            })
            .catch((error) => {
              observer.error(false);
            });
        })
        .catch(() => observer.error(false));
      }
      else{
        let existingMaterial = this.patientMaterials.find(
          (x) => x.TheatrePatientId === theatrePatientId && x.Barcode === barcode && (x.PackId === undefined || x.PackId === null || x.PackId === 0)
        );

        if(existingMaterial){
          existingMaterial.Quantity = quantity;
          existingMaterial.Username = this.authenticationService.getUsername();
          observer.next(true);
        }
        else{
          observer.error(false);
        }
      }
    });
  }

  updateByPackBasketId(packBasketId: string, quantity: number) {
    return new Observable((observer) => {
      if(this.enableLocaldb){
      this.internalDatabaseService
        .getAllByIndex<TheatrePatientMaterials>(this.table, this.packBasketIdIndex, packBasketId)
        .then((existingMaterials) => {
          existingMaterials.forEach((existingMaterial) => {
            existingMaterial.Quantity = quantity;

            this.internalDatabaseService
              .update(this.table, existingMaterial)
              .then(() => {
                observer.next(true);
              })
              .catch((error) => {
                observer.error(false);
              });
          });
        })
        .catch(() => observer.error(false));
      }
      else{
        let existingMaterials = this.patientMaterials.filter((m)=>m.PackBasketId === packBasketId);

        if(existingMaterials){
          existingMaterials.forEach((existingMaterial) => {
            existingMaterial.Quantity = quantity;
          });
          observer.next(true);
        }
        else{
          observer.error(false);
        }
      }
    });
  }



  updateByPackBasketIdAndMaterialBarcode(packBasketId: string, barcode: string, quantity: number) {
    return new Observable((observer) => {
      if(this.enableLocaldb){
      this.internalDatabaseService
        .getAllByIndex<TheatrePatientMaterials>(this.table, this.packBasketIdIndex, packBasketId)
        .then((existingMaterials) => {
          const existingMaterial = existingMaterials.find((x) => x.Barcode == barcode);

          existingMaterial.Quantity = quantity;

          this.internalDatabaseService
            .update(this.table, existingMaterial)
            .then(() => {
              observer.next(true);
            })
            .catch((error) => {
              observer.error(false);
            });
        })
        .catch(() => observer.error(false));
      }
      else{
        let existingMaterial = this.patientMaterials.find((x) => x.PackBasketId === packBasketId && x.Barcode == barcode);
        if(existingMaterial){
          existingMaterial.Quantity = quantity;
          observer.next(true);
        }
        else{
          observer.error(false);
        }
      }
    });
  }

removeByPatientId(theatrePatientId: number): Promise<void> {
    return new Promise((resolve)=> {
      this.getAllByTheatrePatientId(theatrePatientId)
      .subscribe((services)=>{
        if(services){
          services.forEach(async (service)=>{
            await this.remove(service.Id)
          });

            resolve();
        }
        else{
          resolve();
        }
      });
    });
  }

  remove(serviceId: number): Observable<boolean> {
    
    return new Observable<boolean>((observer) => {
      if(this.enableLocaldb){
        this.internalDatabaseService
          .delete(this.table, serviceId)
          .then(() => {
            observer.next(true);
          })
          .catch((error) => {
            observer.error(false);
          });
      }
      else{
        this.patientMaterials = this.patientMaterials.filter((m)=> m.Id !== serviceId);
        observer.next(true);
      }
    });
  }

  async deleteAll() {
    if(this.enableLocaldb){
      await this.internalDatabaseService.deleteAll(this.table);
    }
    else{
      this.patientMaterials = [];
    }
  }

  async deleteByTheatreVisitId(theatrePatientVisitId: number) {
    this.patientMaterials = this.patientMaterials.filter(x => x.TheatrePatientId != theatrePatientVisitId)
  }

  async delete(primaryKey: number) {
    if(this.enableLocaldb){
      return await this.internalDatabaseService.delete(this.table, primaryKey);
    }
    else{
      return new Promise<void>((resolve,reject)=>{ 
        this.patientMaterials = this.patientMaterials.filter((p)=>p.Id !== primaryKey);
        resolve();
      });
    }
  }

  getAllByTheatrePatientId(theatrePatientId: number): Observable<TheatrePatientMaterials[]> {
    return new Observable((observer) => {
      if(this.enableLocaldb){
        this.internalDatabaseService
          .getAllByIndex<TheatrePatientMaterials>(this.table, this.theatrePatientIdIndex, theatrePatientId)
          .then((theatrePatientMaterials) => {
            observer.next(theatrePatientMaterials);
          })
          .catch((error) => {
            observer.error(null);
          });
      }
      else{
        const materials = this.patientMaterials.filter((x)=> x.TheatrePatientId === theatrePatientId);
        observer.next(materials);
      }
    });
  }

  restoreFromBackup(theatrePatientId: number, materialBackup: TheatrePatientMaterials[]) {
    return new Observable((observer) => {
      if(this.enableLocaldb){
        this.getAllByTheatrePatientId(theatrePatientId).subscribe((materials) => {
          if (materials) {
            materials.forEach(async (x) => await this.delete(x.Id));
          }

          const addSubscriptions = Array<Observable<boolean>>();
          if (materialBackup && materialBackup.length > 0) {
            materialBackup.forEach((x) => addSubscriptions.push(this.add(theatrePatientId, x)));
          } else {
            observer.next();
          }

          combineLatest([addSubscriptions]).subscribe(() => {
            observer.next();
          });
      
        });
      }
      else{
          this.patientMaterials = this.patientMaterials.filter((m)=> m.TheatrePatientId !== theatrePatientId);

          const addSubscriptions = Array<Observable<boolean>>();
          if (materialBackup && materialBackup.length > 0) {
            materialBackup.forEach((x) => addSubscriptions.push(this.add(theatrePatientId, x)));
          } else {
            observer.next();
          }

          combineLatest([addSubscriptions]).subscribe(() => {
            observer.next();
          });
      }
    });
  }

  async calculateUpdatedQuantity(stockLocationId: number, materialId: number) {
    let materials: TheatrePatientMaterials[] = [];
    let originalQuantity = 0;
    let updatedQuantity = 0;
    if(this.enableLocaldb){
      const theatrePatientMaterials = await this.internalDatabaseService.getAll<TheatrePatientMaterials>(this.table);
      materials = theatrePatientMaterials.filter((x) => x.StockLocationId === stockLocationId && x.MaterialId === materialId);
    }
    else{
      materials = this.patientMaterials.filter((x)=> x.StockLocationId === stockLocationId && x.MaterialId === materialId );
    }
    if (materials && materials.length > 0) {
      materials.forEach((x) => {
        originalQuantity += x.OriginalQuantity;
        updatedQuantity += x.Quantity;
      });
    }

    return updatedQuantity - originalQuantity;
  }

  map(theatrePatientId: number, item: IMaterial): TheatrePatientMaterials {
    const material = new TheatrePatientMaterials();

    material.Id = item.stockLevelId;
    material.Barcode = item.barcode;
    material.Name = item.name;
    material.Quantity = item.quantity;
    material.OriginalQuantity = item.quantity;
    material.MaterialId = item.stockLevelId;
    material.UnitOfMeasure = item.unitOfMeasure;
    material.TheatrePatientId = theatrePatientId;
    material.StockLocationId = item.stockLocationId;
    material.PackId = item.packId;
    material.PackBasketId = item.packBasketId;
    material.Username = item.username;

    return material;
  }

  mapFromTheatrePatientMaterial(item: TheatrePatientMaterials): IMaterial {
    return {
      barcode: item.Barcode,
      id: item.MaterialId,
      name: item.Name,
      quantity: item.Quantity,
      materialId: item.MaterialId,
      unitOfMeasure: item.UnitOfMeasure,
      stockLocationId: item.StockLocationId,
      stockLevelId: item.MaterialId,
      packId: item.PackId,
      packBasketId: item.PackBasketId,
      username: item.Username
    };
  }

  mapFromStock(theatrePatientId: number, stock: Stock, packId?: number, packBasketId?: string): TheatrePatientMaterials {
    const material = new TheatrePatientMaterials();

    material.Id = stock.stockLevelId;
    material.Barcode = stock.barcode;
    material.Name = stock.name;
    material.Quantity = stock.quantity;
    material.MaterialId = stock.stockLevelId;
    material.UnitOfMeasure = stock.unitOfMeasure;
    material.TheatrePatientId = theatrePatientId;
    material.StockLocationId = stock.stockLocationId;

    if (packId) material.PackId = packId;
    if (packBasketId) material.PackBasketId = packBasketId;

    return material;
  }

  getSettings(){
    this.enableLocaldb = this.settingsService.getBooleanSetting(Settings.ENABLE_INDEXED_DB);
  }
}
